import { useCallback, useMemo } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { GridFilterModel } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';
import { Delete as DeleteIcon, RestoreFromTrash } from '@mui/icons-material';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import { DownloadIcon } from 'components/common/gallery/icons';
import type { ToolbarAction } from 'components/common/FloatingToolbar';
import { useDataGridApi } from 'components/common/withDataGridApi';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useAsyncDialog } from 'components/core/Dialog/common/useAsyncDialog';
import { documentationDownloadDirectoryDialog } from 'components/documentation/documentationDownloadDialog';
import { useProjectTimezone } from 'store/project';
import { confirmDocumentationOperation } from 'components/documentation/documentationOperationDialog';
import { useDocumentationStore } from 'store/documentation';
import { DocumentationViewItem } from 'store/documentation/model/types';
import {
	DocumentationOperationType,
} from 'shared/domain/documentation/operationPreflight';
import { BulkActionMode, getBulkActionState } from '../model';
import { useValueGetters } from './useValueGetters';

const ARCHIVE_RESTORE_LABEL_KEY = {
  restore: CommonTranslationKeys.restore,
  archive: CommonTranslationKeys.archive,
} as const;

export function useDocumentationBulkActions({
	onMoveSelection,
}: {
	onMoveSelection: () => void;
}): {
	actions: ToolbarAction[];
	selectedItemsAmount: number;
	clearSelection: () => void;
} {
	const intl = useIntl();
	const apiRef = useDataGridApi();
	const createDialog = useDialog();
	const createAsyncDialog = useAsyncDialog();
	const selectedItems = useDocumentationStore.use.selectedItems();
	const timezone = useProjectTimezone();

	const prepareSelectedOperation =
		useDocumentationStore.use.prepareSelectedOperation();
	const confirmPreparedOperation =
		useDocumentationStore.use.confirmPreparedOperation();
	const clearPreparedOperation =
		useDocumentationStore.use.clearPreparedOperation();
	const downloadSelected = useDocumentationStore.use.downloadSelected();
	const unselectItems = useDocumentationStore.use.unselectItems();
	const documentState = useDocumentationStore.use.documentState();

	const valueGetters = useValueGetters();

	const selected = useMemo(() => Array.from(selectedItems), [selectedItems]);
	const selectedItemsAmount = selected.length;
	const actionState = getBulkActionState(selected);
	const shouldShowRestore = actionState.mode === BulkActionMode.RESTORE;
	const isRestoreDisabled = actionState.restoreDisabled;

	const handleDownload = useCallback(
		async (items: DocumentationViewItem[], filterModel?: GridFilterModel) => {
			try {
				await createDialog(documentationDownloadDirectoryDialog());
				await downloadSelected(items, {
					filterModel,
					timezone,
					valueGetters,
					documentState,
				});
				unselectItems();
			} catch {
				// Dialog cancelled or error.
			}
		},
		[
			createDialog,
			downloadSelected,
			timezone,
			valueGetters,
			documentState,
			unselectItems,
		]
	);

	const getFilterModel = useCallback(() => {
		return apiRef?.current?.state?.filter?.filterModel;
	}, [apiRef]);

	const handlePreparedOperation = useCallback(
		async (operationType: DocumentationOperationType): Promise<void> => {
			const prepared = await prepareSelectedOperation(operationType);
			if (!prepared || prepared.resolvedCount === 0) {
				return;
			}

			await confirmDocumentationOperation({
				prepared,
				createAsyncDialog,
				confirmPreparedOperation,
				getPreparedOperation: () => useDocumentationStore.getState().preparedOperation,
				clearPreparedOperation,
			});
		},
		[
			clearPreparedOperation,
			confirmPreparedOperation,
			createAsyncDialog,
			prepareSelectedOperation,
		]
	);

	const handleArchive = useCallback(async () => {
		await handlePreparedOperation(DocumentationOperationType.archive);
	}, [handlePreparedOperation]);

	const handleRestore = useCallback(async () => {
		await handlePreparedOperation(DocumentationOperationType.restore);
	}, [handlePreparedOperation]);

	const actions = useMemo<ToolbarAction[]>(
		() => {
			return [
				{
					label: formatTranslation(intl, CommonTranslationKeys.download),
					icon: <DownloadIcon fontSize='small' />,
					onClick: () => handleDownload(selected, getFilterModel()),
				},
				{
					label: formatTranslation(intl, CommonTranslationKeys.move),
					icon: <DriveFileMoveOutlinedIcon fontSize='small' />,
					onClick: onMoveSelection,
				},
				{
					label: formatTranslation(
						intl,
						ARCHIVE_RESTORE_LABEL_KEY[shouldShowRestore ? 'restore' : 'archive']
					),
					icon: shouldShowRestore ? (
						<RestoreFromTrash fontSize='small' />
					) : (
						<DeleteIcon fontSize='small' />
					),
					onClick: shouldShowRestore ? handleRestore : handleArchive,
					disabled: shouldShowRestore ? isRestoreDisabled : false,
				},
			];
		},
		[
			intl,
			shouldShowRestore,
			isRestoreDisabled,
			handleDownload,
			onMoveSelection,
			selected,
			getFilterModel,
			handleRestore,
			handleArchive,
		]
	);

	return {
		actions,
		selectedItemsAmount,
		clearSelection: unselectItems,
	};
}
